import { createContext, useContext, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { Accordion, Spinner } from "react-bootstrap";
import { getBlogPageData, updateBlogPageData } from "../../api/blog";
import { toast } from "react-toastify";
import Label from "../../components/label";
import { FormikInput } from "../../components/input";
import ImageUploader from "../../components/upload-image";
import Button from "../../components/Buttons";

const PageContext = createContext();
const usePageProvider = () => useContext(PageContext);

const PageProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getBlogPageData();
      setData(res || {
        meta_seo: { meta_title: "", meta_keyword: "", meta_desc: "", meta_slug_url: "", },
        banner: { heading: { white: "", orange: "" }, description: "", image: "" },
        section_title: { white: "", orange: "" }
      });
    } catch (error) {
      toast.error("Failed to load blog page settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    try {
      await updateBlogPageData(values);
      toast.success("Blog page settings updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  return (
    <PageContext.Provider value={{ initialValues: data, loading, handleSubmit }}>
      {children}
    </PageContext.Provider>
  );
};

const BlogPageContent = () => {
  const { initialValues, loading, handleSubmit } = usePageProvider();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="my-4">
        <h2 className="fw-bold mb-1 text-[#1a1a4e]">Blog Page Settings</h2>
        <p className="text-muted small mb-0">Customize the main blog listing page appearance and SEO</p>
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Accordion defaultActiveKey="0" className="mb-4">
              <Accordion.Item eventKey="0">
                <Accordion.Header>SEO Meta Information</Accordion.Header>
                <Accordion.Body>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label label="Meta Title" className="mb-1" />
                      <FormikInput name="meta_seo.meta_title" placeholder="Meta Title" />
                    </div>
                    <div>
                      <Label label="Meta Keywords" className="mb-1" />
                      <FormikInput name="meta_seo.meta_keyword" placeholder="Keywords" />
                    </div>
                    <div>
                      <Label label="Meta Slug Url" className="mb-1" />
                      <FormikInput name="meta_seo.meta_slug_url" placeholder="Slug url" />
                    </div>
                    <div>
                      <Label label="Meta Description" className="mb-1 col-span-3 " />
                      <FormikInput name="meta_seo.meta_desc" placeholder="Description" as="textarea" />
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>Hero Banner Settings</Accordion.Header>
                <Accordion.Body>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <Label label="Heading (White)" className="mb-1" />
                        <FormikInput name="banner.heading.white" placeholder="White Title Part" />
                      </div>
                      <div>
                        <Label label="Heading (Orange)" className="mb-1" />
                        <FormikInput name="banner.heading.orange" placeholder="Orange Title Part" />
                      </div>
                      <div>
                        <Label label="Description" className="mb-1" />
                        <FormikInput name="banner.description" placeholder="Short Description" as="textarea" />
                      </div>
                    </div>
                    <div>
                      <Label label="Banner Image" className="mb-2" />
                      <div className="border rounded-xl p-3 bg-light/50">
                        <ImageUploader
                          imgStyle={{ width: "100%", height: "200px", borderRadius: "8px" }}
                          imageUrl={values.banner?.image}
                          onChange={(res) => setFieldValue("banner.image", res?.data?.file_url)}
                        />
                      </div>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>Section Title (Latest Blogs)</Accordion.Header>
                <Accordion.Body>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label label="Heading (White)" className="mb-1" />
                      <FormikInput name="section_title.white" placeholder="Latest Blogs (White)" />
                    </div>
                    <div>
                      <Label label="Heading (Orange)" className="mb-1" />
                      <FormikInput name="section_title.orange" placeholder="Latest Blogs (Orange)" />
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <div className="save-button-bar !bg-white/95 !backdrop-blur-md">
              <Button type="submit" >SAVE BLOG SETTINGS</Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const BlogPageSettings = () => (
  <PageProvider>
    <BlogPageContent />
  </PageProvider>
);

export default BlogPageSettings;
